import type { PendingAction } from './identity-confirmation-store';

/**
 * DEV-555 — ida e volta do provedor social na confirmação de identidade.
 *
 * A reverificação sai do App (`keycloak.login({ prompt: 'login', idpHint })`)
 * e volta por redirect; a ação pendente precisa sobreviver a essa viagem — e
 * SÓ a ela. Por isso ela vive em `sessionStorage` (não na store em memória) e é
 * consumida na primeira leitura do retorno.
 *
 * Resultados possíveis ao voltar (sondados no Keycloak 26.6.3, research.md):
 *   - mesma conta: token novo com `auth_time` > o anterior e o mesmo `sub` → sucesso (AC-30);
 *   - conta diferente: o Keycloak mantém a sessão original e o tema volta com
 *     `?identity-confirmation=different-account` → alerta (AC-31);
 *   - interrompido: a pessoa volta sem token novo → confirmação reaberta, sem alerta (AC-32).
 */
export type ReauthProvider = 'google' | 'linkedin';

export const PROVIDER_IDP_ALIAS: Record<ReauthProvider, string> = {
  google: 'google',
  linkedin: 'linkedin-openid-connect',
};

export const RETURN_PARAM = 'identity-confirmation';
export const RETURN_DIFFERENT_ACCOUNT = 'different-account';
export const RETURN_FROM_PROVIDER = 'return';

const STORAGE_KEY = 'eureca.identity-confirmation.pending';
/** Uma viagem ao provedor não pode durar mais que isso; depois, o retorno é tratado como interrompido. */
const MAX_ROUNDTRIP_MS = 30 * 60 * 1000;

export interface ReauthPending {
  sub: string;
  provider: ReauthProvider;
  action: PendingAction;
  originPath: string;
  authTimeBefore: number;
  startedAt: number;
}

export type ProviderReturn =
  | { kind: 'none' }
  | { kind: 'success'; pending: ReauthPending }
  | { kind: 'different-account'; pending: ReauthPending }
  | { kind: 'interrupted'; pending: ReauthPending };

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function savePending(pending: ReauthPending): void {
  storage()?.setItem(STORAGE_KEY, JSON.stringify(pending));
}

export function readAndClearPending(): ReauthPending | null {
  const s = storage();
  if (!s) return null;
  const raw = s.getItem(STORAGE_KEY);
  s.removeItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ReauthPending;
    if (!parsed || typeof parsed.sub !== 'string' || !parsed.action) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Monta a URL de retorno: a página de origem + marcador de retorno do provedor. */
export function buildReturnUrl(origin: string, originPath: string): string {
  const url = new URL(originPath, origin);
  url.searchParams.set(RETURN_PARAM, RETURN_FROM_PROVIDER);
  return url.toString();
}

export interface TokenIdentity {
  sub?: string;
  auth_time?: number;
}

/** Decide o resultado do retorno a partir do token atual e da URL. Puro, testável. */
export function resolveProviderReturn(input: {
  pending: ReauthPending | null;
  token: TokenIdentity | null | undefined;
  search: string;
  now?: number;
}): ProviderReturn {
  const { pending, token, search } = input;
  if (!pending) return { kind: 'none' };
  const params = new URLSearchParams(search);
  const marker = params.get(RETURN_PARAM);
  if (marker === RETURN_DIFFERENT_ACCOUNT) return { kind: 'different-account', pending };
  const now = input.now ?? Date.now();
  if (now - pending.startedAt > MAX_ROUNDTRIP_MS) return { kind: 'interrupted', pending };
  if (!token?.sub) return { kind: 'interrupted', pending };
  if (token.sub !== pending.sub) return { kind: 'different-account', pending };
  if (typeof token.auth_time === 'number' && token.auth_time > pending.authTimeBefore) {
    return { kind: 'success', pending };
  }
  return { kind: 'interrupted', pending };
}

/** Remove o marcador de retorno da URL sem navegar (o router nunca o vê). */
export function stripReturnParam(href: string): string {
  const url = new URL(href);
  if (!url.searchParams.has(RETURN_PARAM)) return href;
  url.searchParams.delete(RETURN_PARAM);
  return url.pathname + (url.search ? url.search : '') + url.hash;
}
